import { useEffect, useRef, useState } from "react";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { FaArrowRight, FaEdit, FaLink, FaTrash } from "react-icons/fa";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { Button, Dropdown, Form, Modal } from "react-bootstrap";
import RequirementModal from "../components/RequirementModal";
import RequirementBulkEditModal from "../components/RequirementBulkEditModal";
import RequirementFormModal from "../components/RequirementFormModal";
import RequirementSystemLinkModal from "../components/RequirementSystemLinkModal";

const statusFilterOptions = [
  { value: "all", label: "All States" },
  { value: "raw", label: "Raw" },
  { value: "review", label: "Review" },
  { value: "needs_clarification", label: "Needs Clarification" },
  { value: "clarified", label: "Clarified" },
  { value: "targeted", label: "Targeted" },
  { value: "implemented", label: "Implemented" }
];

const coverageFilterOptions = [
  { value: "all", label: "All coverage" },
  { value: "covered", label: "Covered" },
  { value: "notCovered", label: "Not covered" }
];

const systemLinkFilterOptions = [
  { value: "all", label: "All system links" },
  { value: "linked", label: "Linked" },
  { value: "unlinked", label: "Unlinked" }
];

const systemRequirementCategoryOptions = [
  { value: "SW", label: "SW" },
  { value: "SWext", label: "SWext" },
  { value: "HW", label: "HW" },
  { value: "mechanical", label: "Mechanical" },
  { value: "definition", label: "Definition" },
  { value: "informative", label: "Informative" },
  { value: "others", label: "Others" }
];

const statusConfig = {
  raw: {
    label: "Raw",
    color: "#6c757d"
  },
  review: {
    label: "Review",
    color: "#20c997"
  },
  needs_clarification: {
    label: "Needs Clarification",
    color: "#dc3545"
  },
  clarified: {
    label: "Clarified",
    color: "#fdc43f"
  },
  targeted: {
    label: "Targeted",
    color: "#0d6efd"
  },
  implemented: {
    label: "Implemented",
    color: "#6fcb8b"
  }
};

const coverageConfig = {
  covered: {
    label: "Covered",
    color: "#198754"
  },
  notCovered: {
    label: "Not covered",
    color: "#adb5bd"
  }
};

const systemLinkConfig = {
  linked: {
    label: "Linked",
    color: "#198754"
  },
  unlinked: {
    label: "Unlinked",
    color: "#adb5bd"
  }
};

const additionalFilterDefaults = {
  class: [],
  type: [],
  sil: [],
  numberPrefix: []
};

const classFilterOptions = [
  { value: "all", label: "All" },
  { value: "sys", label: "System" },
  { value: "sw", label: "Software" },
  { value: "none", label: "none" }
];

const typeFilterOptions = [
  { value: "all", label: "All" },
  { value: "Functional", label: "Functional" },
  { value: "Non Functional", label: "Non Functional" },
  { value: "Info", label: "Info" },
  { value: "Performance", label: "Performance" },
  { value: "none", label: "none" }
];

const silFilterOptions = [
  { value: "all", label: "All" },
  { value: "0", label: "0" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "none", label: "none" }
];

const numberPrefixFilterOptions = [
  { value: "all", label: "All" },
  { value: "DMI", label: "ETCS DMI" },
  { value: "ss_121", label: "Subset-121" },
  { value: "ss_092_1", label: "Subset-092-1" },
  { value: "ss_104", label: "Subset-104" },
  { value: "ss_026", label: "Subset-026" },
  { value: "ss_035", label: "Subset-035" },
  { value: "INT", label: "Internal" }
];

const pageSizeOptions = [50, 100, 250];

const aiDisplayConfig = {
  sw: { label: "sw", className: "text-bg-primary" },
  sys: { label: "sys", className: "text-bg-success" },
  info: { label: "info", className: "text-bg-info" },
  def: { label: "def", className: "text-bg-warning" },
  oth: { label: "oth", className: "text-bg-secondary" },
  "-": { label: "-", className: "text-bg-light" }
};

function getSelectedFilterCount(filters) {
  return Object.values(filters).reduce((count, values) => count + values.length, 0);
}

export default function Requirements({ user }) {
  const tableContainerRef = useRef(null);
  const [requirements, setRequirements] = useState([]);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [linkingRequirement, setLinkingRequirement] = useState(null);
  const [movingRequirement, setMovingRequirement] = useState(null);
  const [moveCategory, setMoveCategory] = useState("SW");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [coverageFilter, setCoverageFilter] = useState("all");
  const [systemLinkFilter, setSystemLinkFilter] = useState("all");
  const [hideHeadings, setHideHeadings] = useState(false);
  const [additionalFilters, setAdditionalFilters] = useState(additionalFilterDefaults);
  const [statusChartView, setStatusChartView] = useState("donut");
  const [sortField, setSortField] = useState("number");
  const [sortDir, setSortDir] = useState("asc");
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRequirements, setTotalRequirements] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [coverageCounts, setCoverageCounts] = useState({ covered: 0, notCovered: 0 });
  const [systemLinkCounts, setSystemLinkCounts] = useState({ linked: 0, unlinked: 0 });
  const roleCodes = user?.roles ?? [];
  const canAddDelete = roleCodes.includes("PM") || roleCodes.includes("RQM");
  const canEdit = canAddDelete || roleCodes.includes("IMP");
  const currentFilterLabel = statusFilterOptions.find((option) => option.value === statusFilter)?.label ?? "All States";
  const currentCoverageFilterLabel = coverageFilterOptions.find((option) => option.value === coverageFilter)?.label ?? "All coverage";
  const currentSystemLinkFilterLabel = systemLinkFilterOptions.find((option) => option.value === systemLinkFilter)?.label ?? "All system links";
  const activeAdditionalFilterCount = getSelectedFilterCount(additionalFilters);
  const hasActiveFilters = (
    statusFilter !== "all" ||
    coverageFilter !== "all" ||
    systemLinkFilter !== "all" ||
    hideHeadings ||
    activeAdditionalFilterCount > 0
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPage(1);
      setDebouncedSearch(search);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [search]);

  useEffect(() => {
    loadRequirements();
  }, [page, pageSize, debouncedSearch, statusFilter, coverageFilter, systemLinkFilter, hideHeadings, additionalFilters, sortField, sortDir]);

  useEffect(() => {
    if (!tableContainerRef.current) return;

    tableContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    tableContainerRef.current.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [page, pageSize]);

  const loadRequirements = async () => {
	try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        sortField,
        sortDir
      });

      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (coverageFilter !== "all") params.set("coverage", coverageFilter);
      if (systemLinkFilter !== "all") params.set("systemLink", systemLinkFilter);
      if (hideHeadings) params.set("hideHeadings", "true");
      if (additionalFilters.class.length) params.set("class", additionalFilters.class.join(","));
      if (additionalFilters.type.length) params.set("type", additionalFilters.type.join(","));
      if (additionalFilters.sil.length) params.set("sil", additionalFilters.sil.join(","));
      if (additionalFilters.numberPrefix.length) params.set("reference", additionalFilters.numberPrefix.join(","));

	  const res = await fetchWithAuth(`/api/requirements?${params.toString()}`);
  
	  if (!res.ok) {
		console.error("Failed to load requirements");
		return;
	  }
  
	  const data = await res.json();
      setRequirements(data.rows ?? []);
      setTotalRequirements(data.total ?? 0);
      setStatusCounts(data.statusCounts ?? {});
      setCoverageCounts(data.coverageCounts ?? { covered: 0, notCovered: 0 });
      setSystemLinkCounts(data.systemLinkCounts ?? { linked: 0, unlinked: 0 });
      setSelectedIds([]);
	} catch (err) {
	  console.warn("loadRequirements aborted:", err.message);
	}
  };
  
  const openRequirement = async (id) => {
    const res = await fetchWithAuth(`/api/requirements/${id}`);

    if (!res.ok) return;

    const data = await res.json();

    setSelectedRequirement(data);
    setShowModal(true);
  };

  const deleteRequirement = async (id) => {

    if (!window.confirm("Delete this requirement?")) return;

    const res = await fetchWithAuth(`/api/requirements/${id}`, {
      method: "DELETE"
    });

    if (!res.ok) {
      alert("Delete failed");
      return;
    }

    loadRequirements();
  };

  const moveRequirementToSystem = async () => {
    if (!movingRequirement) return;
    
    const res = await fetchWithAuth(`/api/requirements/${movingRequirement.id}/move-to-system`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ category: moveCategory })
    });

    if (!res.ok) {
      const error = await res.json().catch(() => null);
      alert(error?.error || "Move to system failed");
      return;
    }

    setMovingRequirement(null);
    loadRequirements();
  };
  
  const handleSort = (field) => {
	if (field === sortField) {
	  setSortDir(sortDir === "asc" ? "desc" : "asc");
	} else {
	  setSortField(field);
	  setSortDir("asc");
	}
    setPage(1);
  };

  const visibleSelectableRequirements = requirements.filter((requirement) => requirement.nodeType !== "section");
  const allVisibleSelected = visibleSelectableRequirements.length > 0 && visibleSelectableRequirements.every((requirement) => selectedIds.includes(requirement.id));
  const selectedCount = selectedIds.length;
  const statusTotal = Object.values(statusCounts).reduce((sum, count) => sum + Number(count ?? 0), 0);
  const coverageTotal = Number(coverageCounts.covered ?? 0) + Number(coverageCounts.notCovered ?? 0);
  const systemLinkTotal = Number(systemLinkCounts.linked ?? 0) + Number(systemLinkCounts.unlinked ?? 0);
  const pageCount = Math.max(1, Math.ceil(totalRequirements / pageSize));
  const pageStart = totalRequirements > 0 ? ((page - 1) * pageSize) + 1 : 0;
  const pageEnd = Math.min(page * pageSize, totalRequirements);
  const statusSegments = Object.entries(statusConfig)
    .map(([status, config]) => {
      const count = statusCounts[status] ?? 0;
      const percent = statusTotal > 0 ? (count / statusTotal) * 100 : 0;

      return {
        status,
        label: config.label,
        color: config.color,
        count,
        percent
      };
    })
    .filter((segment) => segment.count > 0);
  const coverageSegments = Object.entries(coverageConfig)
    .map(([coverage, config]) => {
      const count = Number(coverageCounts[coverage] ?? 0);
      const percent = coverageTotal > 0 ? (count / coverageTotal) * 100 : 0;

      return {
        coverage,
        label: config.label,
        color: config.color,
        count,
        percent
      };
    })
    .filter((segment) => segment.count > 0);
  const systemLinkSegments = Object.entries(systemLinkConfig)
    .map(([systemLink, config]) => {
      const count = Number(systemLinkCounts[systemLink] ?? 0);
      const percent = systemLinkTotal > 0 ? (count / systemLinkTotal) * 100 : 0;

      return {
        systemLink,
        label: config.label,
        color: config.color,
        count,
        percent
      };
    })
    .filter((segment) => segment.count > 0);
  const displayedSystemLinkSegments = systemLinkSegments.length
    ? systemLinkSegments
    : Object.entries(systemLinkConfig).map(([systemLink, config]) => ({
      systemLink,
      label: config.label,
      color: config.color,
      count: 0,
      percent: 0
    }));

  const buildDonutBackground = (segments) => {
    const stops = [];
    let currentOffset = 0;

    segments.forEach((segment) => {
      const start = currentOffset;
      const end = currentOffset + segment.percent;
      stops.push(`${segment.color} ${start}% ${end}%`);
      currentOffset = end;
    });

    return segments.length
      ? `conic-gradient(${stops.join(", ")})`
      : "#e9ecef";
  };
  const statusDonutBackground = buildDonutBackground(statusSegments);
  const coverageDonutBackground = buildDonutBackground(coverageSegments);
  const systemLinkDonutBackground = buildDonutBackground(systemLinkSegments);

  const renderHighlightedText = (value) => {
    const text = String(value ?? "");
    const query = debouncedSearch.trim();

    if (!query) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const parts = [];
    let startIndex = 0;
    let matchIndex = lowerText.indexOf(lowerQuery);

    while (matchIndex !== -1) {
      if (matchIndex > startIndex) {
        parts.push(text.slice(startIndex, matchIndex));
      }

      const endIndex = matchIndex + query.length;
      parts.push(
        <mark key={`${text}-${matchIndex}`} className="px-0 bg-warning-subtle">
          {text.slice(matchIndex, endIndex)}
        </mark>
      );

      startIndex = endIndex;
      matchIndex = lowerText.indexOf(lowerQuery, startIndex);
    }

    if (startIndex < text.length) {
      parts.push(text.slice(startIndex));
    }

    return parts.length ? parts : text;
  };

  const renderNotePreview = (value, maxLength = 90) => {
    const text = String(value ?? "");

    if (!text) return "";

    const compactText = text.replace(/\s+/g, " ").trim();
    const preview = compactText.length > maxLength
      ? `${compactText.slice(0, maxLength).trim()}...`
      : compactText;

    return (
      <span title={text}>
        {renderHighlightedText(preview)}
      </span>
    );
  };

  const renderAiValue = (value) => {
    const key = String(value ?? "").trim();
    if (!key) return "";

    const config = aiDisplayConfig[key] ?? { label: key, className: "text-bg-light" };

    return (
      <span className={`badge ${config.className}`}>
        {renderHighlightedText(config.label)}
      </span>
    );
  };

  const renderSortIndicator = (field) => {
    if (sortField !== field) {
      return <FaSort className="ms-1 text-muted" aria-hidden="true" />;
    }

    if (sortDir === "asc") {
      return <FaSortUp className="ms-1" aria-hidden="true" />;
    }

    return <FaSortDown className="ms-1" aria-hidden="true" />;
  };

  const stickyHeaderStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "var(--bs-table-bg, #f8f9fa)"
  };

  const requirementColumnCount = canEdit ? 11 : 9;

  const hasSystemRequirementLinks = (requirement) => (requirement.SystemRequirements ?? []).length > 0;

  const getRowStyle = (index, requirement) => ({
    "--bs-table-bg": index % 2 === 0 ? "rgba(25, 135, 84, 0.06)" : "#ffffff",
    ...(hasSystemRequirementLinks(requirement) ? {
      borderLeft: "4px solid rgba(25, 135, 84, 0.85)"
    } : {})
  });

  const sectionRowStyle = {
    "--bs-table-bg": "rgba(13, 110, 253, 0.12)",
    borderTop: "2px solid rgba(13, 110, 253, 0.24)"
  };

  const toggleRequirementSelection = (id) => {
    setSelectedIds((current) => (
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id]
    ));
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((current) => current.filter((id) => !visibleSelectableRequirements.some((requirement) => requirement.id === id)));
      return;
    }

    setSelectedIds((current) => {
      const visibleIds = visibleSelectableRequirements.map((requirement) => requirement.id);
      return [...new Set([...current, ...visibleIds])];
    });
  };

  const openBulkEdit = () => {
    if (!selectedCount) {
      alert("Select at least one requirement");
      return;
    }

    setShowBulkEdit(true);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleCoverageFilterChange = (value) => {
    setCoverageFilter(value);
    setPage(1);
  };

  const handleSystemLinkFilterChange = (value) => {
    setSystemLinkFilter(value);
    setPage(1);
  };

  const toggleAdditionalFilter = (field, value) => {
    setAdditionalFilters((current) => ({
      ...current,
      [field]: current[field].includes(value)
        ? current[field].filter((item) => item !== value)
        : [...current[field], value]
    }));
    setPage(1);
  };

  const clearAdditionalFilters = () => {
    setAdditionalFilters(additionalFilterDefaults);
    setPage(1);
  };

  const clearTableFilters = () => {
    setStatusFilter("all");
    setCoverageFilter("all");
    setSystemLinkFilter("all");
    setHideHeadings(false);
    setAdditionalFilters(additionalFilterDefaults);
    setPage(1);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setPage(1);
  };

  return (
    <div className="container mt-4">

      <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="position-relative" style={{ width: "320px", maxWidth: "100%" }}>
            <input
		      type="text"
		      className="form-control pe-5"
		      placeholder="Search requirements..."
		      value={search}
		      onChange={(e) => setSearch(e.target.value)}
		    />
            {search && (
              <button
                type="button"
                className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent text-muted"
                onClick={() => {
                  setSearch("");
                  setDebouncedSearch("");
                  setPage(1);
                }}
                aria-label="Clear search"
              >
                x
              </button>
            )}
          </div>

          {hasActiveFilters && (
            <>
              <span className="badge text-bg-warning">Filtered search</span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={clearTableFilters}
                title="Clear State, Coverage, and Filters"
              >
                Clear filters
              </button>
            </>
          )}

          <Dropdown>
            <Dropdown.Toggle variant={statusFilter === "all" ? "outline-secondary" : "secondary"}>
              {currentFilterLabel}
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {statusFilterOptions.map((option) => (
                <Dropdown.Item
                  key={option.value}
                  active={statusFilter === option.value}
                  onClick={() => handleStatusFilterChange(option.value)}
                >
                  {option.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown>
            <Dropdown.Toggle variant={coverageFilter === "all" ? "outline-secondary" : "secondary"}>
              {currentCoverageFilterLabel}
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {coverageFilterOptions.map((option) => (
                <Dropdown.Item
                  key={option.value}
                  active={coverageFilter === option.value}
                  onClick={() => handleCoverageFilterChange(option.value)}
                >
                  {option.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown>
            <Dropdown.Toggle variant={systemLinkFilter === "all" ? "outline-secondary" : "secondary"}>
              {currentSystemLinkFilterLabel}
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {systemLinkFilterOptions.map((option) => (
                <Dropdown.Item
                  key={option.value}
                  active={systemLinkFilter === option.value}
                  onClick={() => handleSystemLinkFilterChange(option.value)}
                >
                  {option.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown autoClose="outside">
            <Dropdown.Toggle variant={activeAdditionalFilterCount ? "secondary" : "outline-secondary"}>
              Filters{activeAdditionalFilterCount ? ` (${activeAdditionalFilterCount})` : ""}
            </Dropdown.Toggle>

            <Dropdown.Menu className="p-3" style={{ minWidth: "260px" }}>
              <div className="mb-3">
                <div className="form-label small text-muted mb-1">
                  Class
                </div>
                {classFilterOptions.filter((option) => option.value !== "all").map((option) => (
                  <label key={option.value} className="form-check mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={additionalFilters.class.includes(option.value)}
                      onChange={() => toggleAdditionalFilter("class", option.value)}
                    />
                    <span className="form-check-label">{option.label}</span>
                  </label>
                ))}
              </div>

              <div className="mb-3">
                <div className="form-label small text-muted mb-1">
                  Type
                </div>
                {typeFilterOptions.filter((option) => option.value !== "all").map((option) => (
                  <label key={option.value} className="form-check mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={additionalFilters.type.includes(option.value)}
                      onChange={() => toggleAdditionalFilter("type", option.value)}
                    />
                    <span className="form-check-label">{option.label}</span>
                  </label>
                ))}
              </div>

              <div className="mb-3">
                <div className="form-label small text-muted mb-1">
                  SIL
                </div>
                {silFilterOptions.filter((option) => option.value !== "all").map((option) => (
                  <label key={option.value} className="form-check mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={additionalFilters.sil.includes(option.value)}
                      onChange={() => toggleAdditionalFilter("sil", option.value)}
                    />
                    <span className="form-check-label">{option.label}</span>
                  </label>
                ))}
              </div>

              <div className="mb-3">
                <div className="form-label small text-muted mb-1">
                  Reference
                </div>
                {numberPrefixFilterOptions.filter((option) => option.value !== "all").map((option) => (
                  <label key={option.value} className="form-check mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={additionalFilters.numberPrefix.includes(option.value)}
                      onChange={() => toggleAdditionalFilter("numberPrefix", option.value)}
                    />
                    <span className="form-check-label">{option.label}</span>
                  </label>
                ))}
              </div>

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary w-100"
                onClick={clearAdditionalFilters}
                disabled={!activeAdditionalFilterCount}
              >
                Clear filters
              </button>
            </Dropdown.Menu>
          </Dropdown>

          <div className="form-check form-switch m-0">
            <input
              id="requirements-hide-headings"
              type="checkbox"
              className="form-check-input"
              checked={hideHeadings}
              onChange={(e) => {
                setHideHeadings(e.target.checked);
                setPage(1);
              }}
            />
            <label className="form-check-label" htmlFor="requirements-hide-headings">
              Hide headings
            </label>
          </div>
        </div>
        {(canAddDelete || canEdit) && (
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {canEdit && (
              <button
                className="btn btn-outline-secondary"
                onClick={openBulkEdit}
                disabled={!selectedCount}
              >
                Bulk edit{selectedCount ? ` (${selectedCount})` : ""}
              </button>
            )}
            {canAddDelete && (
              <button
                className="btn btn-success"
                onClick={() => {
                  setEditingRequirement(null);
                  setShowForm(true);
                }}
              >
                Add requirement
              </button>
            )}
          </div>
        )}
      </div>

      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-2">
          <div className="small text-muted">
            Distribution for {statusTotal} requirement{statusTotal === 1 ? "" : "s"}
          </div>

          <div className="btn-group btn-group-sm" role="group" aria-label="Status chart view">
            <button
              type="button"
              className={`btn ${statusChartView === "bar" ? "btn-secondary" : "btn-outline-secondary"}`}
              onClick={() => setStatusChartView("bar")}
            >
              Bar
            </button>
            <button
              type="button"
              className={`btn ${statusChartView === "donut" ? "btn-secondary" : "btn-outline-secondary"}`}
              onClick={() => setStatusChartView("donut")}
            >
              Donut
            </button>
          </div>
        </div>

        {statusChartView === "donut" ? (
          <div className="d-flex align-items-start gap-5 flex-wrap">
            <div className="d-flex align-items-center gap-3">
              <div className="position-relative" style={{ width: "112px", height: "112px" }}>
                <div
                  style={{
                    width: "112px",
                    height: "112px",
                    borderRadius: "50%",
                    background: statusDonutBackground,
                    boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.06)"
                  }}
                />
                <div
                  className="position-absolute top-50 start-50 translate-middle rounded-circle bg-white d-flex flex-column align-items-center justify-content-center"
                  style={{
                    width: "64px",
                    height: "64px",
                    boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.04)"
                  }}
                >
                  <div className="fw-semibold" style={{ fontSize: "1.1rem", lineHeight: 1 }}>
                    {statusTotal}
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.68rem", lineHeight: 1.1 }}>
                    status
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column gap-2 small">
                {statusSegments.map((segment) => (
                  <button
                    key={segment.status}
                    type="button"
                    className="btn btn-link p-0 text-decoration-none text-reset d-inline-flex align-items-center gap-2 text-start"
                    onClick={() => handleStatusFilterChange(segment.status)}
                  >
                    <span
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "999px",
                        display: "inline-block",
                        backgroundColor: segment.color
                      }}
                    />
                    <span>
                      {segment.label}: {segment.count} ({Math.round(segment.percent)}%)
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="d-flex align-items-center gap-3">
              <div className="position-relative" style={{ width: "112px", height: "112px" }}>
                <div
                  style={{
                    width: "112px",
                    height: "112px",
                    borderRadius: "50%",
                    background: coverageDonutBackground,
                    boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.06)"
                  }}
                />
                <div
                  className="position-absolute top-50 start-50 translate-middle rounded-circle bg-white d-flex flex-column align-items-center justify-content-center"
                  style={{
                    width: "64px",
                    height: "64px",
                    boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.04)"
                  }}
                >
                  <div className="fw-semibold" style={{ fontSize: "1.1rem", lineHeight: 1 }}>
                    {coverageTotal}
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.68rem", lineHeight: 1.1 }}>
                    coverage
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column gap-2 small">
                {coverageSegments.map((segment) => (
                  <button
                    key={segment.coverage}
                    type="button"
                    className="btn btn-link p-0 text-decoration-none text-reset d-inline-flex align-items-center gap-2 text-start"
                    onClick={() => handleCoverageFilterChange(segment.coverage)}
                  >
                    <span
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "999px",
                        display: "inline-block",
                        backgroundColor: segment.color
                      }}
                    />
                    <span>
                      {segment.label}: {segment.count} ({Math.round(segment.percent)}%)
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="d-flex align-items-center gap-3">
              <div className="position-relative" style={{ width: "112px", height: "112px" }}>
                <div
                  style={{
                    width: "112px",
                    height: "112px",
                    borderRadius: "50%",
                    background: systemLinkDonutBackground,
                    boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.06)"
                  }}
                />
                <div
                  className="position-absolute top-50 start-50 translate-middle rounded-circle bg-white d-flex flex-column align-items-center justify-content-center"
                  style={{
                    width: "64px",
                    height: "64px",
                    boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.04)"
                  }}
                >
                  <div className="fw-semibold" style={{ fontSize: "1.1rem", lineHeight: 1 }}>
                    {systemLinkTotal}
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.68rem", lineHeight: 1.1 }}>
                    links
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column gap-2 small">
                {displayedSystemLinkSegments.map((segment) => (
                  <button
                    key={segment.systemLink}
                    type="button"
                    className="btn btn-link p-0 text-decoration-none text-reset d-inline-flex align-items-center gap-2 text-start"
                    onClick={() => handleSystemLinkFilterChange(segment.systemLink)}
                  >
                    <span
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "999px",
                        display: "inline-block",
                        backgroundColor: segment.color
                      }}
                    />
                    <span>
                      {segment.label}: {segment.count} ({Math.round(segment.percent)}%)
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="small text-muted mb-1">Status</div>
            <div
              style={{
                height: "14px",
                borderRadius: "999px",
                overflow: "hidden",
                backgroundColor: "#e9ecef",
                display: "flex"
              }}
            >
              {statusSegments.map((segment) => (
                <button
                  key={segment.status}
                  type="button"
                  title={`${segment.label}: ${segment.count} (${Math.round(segment.percent)}%)`}
                  onClick={() => handleStatusFilterChange(segment.status)}
                  style={{
                    width: `${segment.percent}%`,
                    backgroundColor: segment.color,
                    minWidth: segment.percent > 0 ? "6px" : "0",
                    border: "0",
                    padding: 0,
                    margin: 0,
                    cursor: "pointer"
                  }}
                />
              ))}
            </div>

            <div className="d-flex align-items-center gap-3 flex-wrap small mt-2">
              {statusSegments.map((segment) => (
                <button
                  key={segment.status}
                  type="button"
                  className="btn btn-link p-0 text-decoration-none text-reset d-inline-flex align-items-center gap-2"
                  onClick={() => handleStatusFilterChange(segment.status)}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "999px",
                      display: "inline-block",
                      backgroundColor: segment.color
                    }}
                  />
                  <span>
                    {segment.label}: {segment.count} ({Math.round(segment.percent)}%)
                  </span>
                </button>
              ))}
            </div>

            <div className="small text-muted mt-3 mb-1">Coverage</div>
            <div
              style={{
                height: "14px",
                borderRadius: "999px",
                overflow: "hidden",
                backgroundColor: "#e9ecef",
                display: "flex"
              }}
            >
              {coverageSegments.map((segment) => (
                <button
                  key={segment.coverage}
                  type="button"
                  title={`${segment.label}: ${segment.count} (${Math.round(segment.percent)}%)`}
                  onClick={() => handleCoverageFilterChange(segment.coverage)}
                  style={{
                    width: `${segment.percent}%`,
                    backgroundColor: segment.color,
                    minWidth: segment.percent > 0 ? "6px" : "0",
                    border: "0",
                    padding: 0,
                    margin: 0,
                    cursor: "pointer"
                  }}
                />
              ))}
            </div>

            <div className="d-flex align-items-center gap-3 flex-wrap small mt-2">
              {coverageSegments.map((segment) => (
                <button
                  key={segment.coverage}
                  type="button"
                  className="btn btn-link p-0 text-decoration-none text-reset d-inline-flex align-items-center gap-2"
                  onClick={() => handleCoverageFilterChange(segment.coverage)}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "999px",
                      display: "inline-block",
                      backgroundColor: segment.color
                    }}
                  />
                  <span>
                    {segment.label}: {segment.count} ({Math.round(segment.percent)}%)
                  </span>
                </button>
              ))}
            </div>

            <div className="small text-muted mt-3 mb-1">System links</div>
            <div
              style={{
                height: "14px",
                borderRadius: "999px",
                overflow: "hidden",
                backgroundColor: "#e9ecef",
                display: "flex"
              }}
            >
              {displayedSystemLinkSegments.map((segment) => (
                <button
                  key={segment.systemLink}
                  type="button"
                  title={`${segment.label}: ${segment.count} (${Math.round(segment.percent)}%)`}
                  onClick={() => handleSystemLinkFilterChange(segment.systemLink)}
                  style={{
                    width: `${segment.percent}%`,
                    backgroundColor: segment.color,
                    minWidth: segment.percent > 0 ? "6px" : "0",
                    border: "0",
                    padding: 0,
                    margin: 0,
                    cursor: "pointer"
                  }}
                />
              ))}
            </div>

            <div className="d-flex align-items-center gap-3 flex-wrap small mt-2">
              {displayedSystemLinkSegments.map((segment) => (
                <button
                  key={segment.systemLink}
                  type="button"
                  className="btn btn-link p-0 text-decoration-none text-reset d-inline-flex align-items-center gap-2"
                  onClick={() => handleSystemLinkFilterChange(segment.systemLink)}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "999px",
                      display: "inline-block",
                      backgroundColor: segment.color
                    }}
                  />
                  <span>
                    {segment.label}: {segment.count} ({Math.round(segment.percent)}%)
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div ref={tableContainerRef} style={{ maxHeight: "60vh", overflowY: "auto" }}>
      <table className="table table-hover align-middle mb-0">
	  
		<thead className="table-light">
		  <tr>
            {canEdit && (
              <th style={{ ...stickyHeaderStyle, width: "44px" }}>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAllVisible}
                  aria-label="Select all visible requirements"
                />
              </th>
            )}
			<th
              style={{ ...stickyHeaderStyle, cursor: "pointer" }}
              onClick={() => handleSort("number")}
            >
              <span className="d-inline-flex align-items-center">
                Number
                {renderSortIndicator("number")}
              </span>
            </th>
			<th
              style={{ ...stickyHeaderStyle, cursor: "pointer" }}
              onClick={() => handleSort("name")}
            >
              <span className="d-inline-flex align-items-center">
                Name
                {renderSortIndicator("name")}
              </span>
            </th>
			<th style={{ ...stickyHeaderStyle, width:"360px" }}>Quote</th>
			<th
              style={{ ...stickyHeaderStyle, width: "80px", cursor: "pointer" }}
              onClick={() => handleSort("sil")}
            >
              <span className="d-inline-flex align-items-center">
                SIL
                {renderSortIndicator("sil")}
              </span>
            </th>
			<th
              style={{ ...stickyHeaderStyle, width: "90px", cursor: "pointer" }}
              onClick={() => handleSort("class")}
            >
              <span className="d-inline-flex align-items-center">
                Class
                {renderSortIndicator("class")}
              </span>
            </th>
			<th
              style={{ ...stickyHeaderStyle, width: "70px", cursor: "pointer" }}
              onClick={() => handleSort("ai")}
            >
              <span className="d-inline-flex align-items-center">
                AI
                {renderSortIndicator("ai")}
              </span>
            </th>
			<th
              style={{ ...stickyHeaderStyle, width: "120px", cursor: "pointer" }}
              onClick={() => handleSort("type")}
            >
              <span className="d-inline-flex align-items-center">
                Type
                {renderSortIndicator("type")}
              </span>
            </th>
			<th
              style={{ ...stickyHeaderStyle, width: "140px", cursor: "pointer" }}
              onClick={() => handleSort("status")}
            >
              <span className="d-inline-flex align-items-center">
                State
                {renderSortIndicator("status")}
              </span>
            </th>
			<th style={{ ...stickyHeaderStyle, width:"120px" }}>Note</th>
			{canEdit && <th style={{ ...stickyHeaderStyle, width:"200px" }}>Actions</th>}
		  </tr>
		</thead>
	  
		<tbody>
		  {requirements.map((r, index) => {
            const isSection = r.nodeType === "section";

            if (isSection) {
              return (
                <tr key={r.id} style={sectionRowStyle}>
                  <td colSpan={requirementColumnCount} className="py-2">
                    <div className="d-flex align-items-baseline gap-3">
                      <span className="fw-bold text-primary">
                        {renderHighlightedText(r.number)}
                      </span>
                      <span className="fw-semibold">
                        {renderHighlightedText(r.name)}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            }

            return (
			<tr key={r.id} style={getRowStyle(index, r)}>
              {canEdit && (
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(r.id)}
                    onChange={() => toggleRequirementSelection(r.id)}
                    aria-label={`Select requirement ${r.number}`}
                  />
                </td>
              )}
	  
			  <td>
				<button
				  className="btn btn-link p-0 fw-bold"
				  onClick={() => openRequirement(r.id)}
				>
				  {renderHighlightedText(r.number)}
				</button>
			  </td>
	  
			  <td>{renderHighlightedText(r.name)}</td>

			  <td style={{ whiteSpace: "pre-wrap" }}>{renderHighlightedText(r.quote)}</td>
	  
			  <td>
				<span className="badge bg-secondary">
				  {renderHighlightedText(r.sil)}
				</span>
			  </td>

			  <td>{renderHighlightedText(r.class)}</td>

			  <td>{renderAiValue(r.ai)}</td>
	  
			  <td>{renderHighlightedText(r.type)}</td>
			  
			  <td>
                <span
                  className="badge"
                  style={{
                    backgroundColor: statusConfig[r.status]?.color ?? "#6c757d",
                    color: r.status === "clarified" ? "#212529" : "#ffffff"
                  }}
                >
                  {statusConfig[r.status]?.label ?? r.status ?? ""}
                </span>
              </td>

			  <td>{renderNotePreview(r.note)}</td>
	  
			  {canEdit && (
			    <td>
			      <button
			        className="btn btn-sm btn-outline-primary me-2"
			        onClick={() => {
                      setEditingRequirement(r);
                      setShowForm(true);
                    }}
			      >
			        <FaEdit />
			      </button>

			      {canAddDelete && (
              <>
                <button
                  className="btn btn-sm btn-outline-success me-2"
                  onClick={() => setLinkingRequirement(r)}
                  title={hasSystemRequirementLinks(r) ? "Edit System Requirement link" : "Link System Requirement"}
                  aria-label={hasSystemRequirementLinks(r) ? "Edit System Requirement link" : "Link System Requirement"}
                >
                  <FaLink />
                </button>

                <button
                  className="btn btn-sm btn-outline-success me-2"
                  onClick={() => {
                    setMovingRequirement(r);
                    setMoveCategory("SW");
                  }}
                  disabled={hasSystemRequirementLinks(r)}
                  title={hasSystemRequirementLinks(r) ? "Already linked to System Requirements" : "Move to System Requirements"}
                  aria-label="Move to System Requirements"
                >
                  <FaArrowRight />
                </button>

			          <button
			            className="btn btn-sm btn-outline-danger"
			            onClick={() => deleteRequirement(r.id)}
			          >
			            <FaTrash />
			          </button>
              </>
			      )}
			    </td>
			  )}
	  
			</tr>
            );
          })}
		</tbody>
	  
	  </table>
      </div>

      <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mt-3">
        <div className="small text-muted">
          Showing {pageStart}-{pageEnd} of {totalRequirements}
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <label className="small text-muted" htmlFor="requirements-page-size">
            Rows
          </label>
          <select
            id="requirements-page-size"
            className="form-select form-select-sm"
            style={{ width: "90px" }}
            value={pageSize}
            onChange={(event) => handlePageSizeChange(event.target.value)}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>

          <div className="btn-group btn-group-sm" role="group" aria-label="Requirements pagination">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setPage(1)}
              disabled={page <= 1}
            >
              First
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>
            <button type="button" className="btn btn-outline-secondary" disabled>
              {page} / {pageCount}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
              disabled={page >= pageCount}
            >
              Next
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setPage(pageCount)}
              disabled={page >= pageCount}
            >
              Last
            </button>
          </div>
        </div>
      </div>

      <RequirementModal
        show={showModal}
        onClose={() => setShowModal(false)}
        requirement={selectedRequirement}
      />

      <RequirementBulkEditModal
        show={showBulkEdit}
        onClose={() => setShowBulkEdit(false)}
        onSaved={() => {
          setSelectedIds([]);
          loadRequirements();
        }}
        selectedCount={selectedCount}
        selectedIds={selectedIds}
      />
      
      <RequirementFormModal
        show={showForm}
        onClose={() => setShowForm(false)}
        onSaved={loadRequirements}
        requirement={editingRequirement}
      />

      <RequirementSystemLinkModal
        show={Boolean(linkingRequirement)}
        onClose={() => setLinkingRequirement(null)}
        onSaved={loadRequirements}
        requirement={linkingRequirement}
      />

      <Modal show={Boolean(movingRequirement)} onHide={() => setMovingRequirement(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Move to System Requirements</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Move <strong>{movingRequirement?.number}</strong> to System Requirements?
          </p>

          <Form.Group>
            <Form.Label>Category</Form.Label>
            <Form.Select
              value={moveCategory}
              onChange={(event) => setMoveCategory(event.target.value)}
            >
              {systemRequirementCategoryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setMovingRequirement(null)}>
            Cancel
          </Button>
          <Button variant="success" onClick={moveRequirementToSystem}>
            Move
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
}
